// Provider uncertainty and pending financial work take precedence over legacy
// failed/completed flags, so an unresolved job never appears fully settled.
export const RECONCILIATION = ["submission_unknown", "reservation_unknown", "settling_unknown", "releasing_unknown", "timeout", "reconciling"];

export const needsReview = (job) =>
  job?.status === "reconciling" || RECONCILIATION.includes(job?.provider_status) ||
  RECONCILIATION.includes(job?.credit_status) ||
  (["completed", "failed"].includes(job?.status) && ["reserving", "reserved", "settling", "releasing"].includes(job?.credit_status));

// Delayed reads must not overwrite a newer lifecycle snapshot for the same job.
export const isOlderSnapshot = (current, incoming) => {
  if (current?.id !== incoming?.id) return false;
  const before = Date.parse(current?.updated_at);
  const after = Date.parse(incoming?.updated_at);
  if (!Number.isFinite(before) || !Number.isFinite(after)) return false;
  if (after !== before) return after < before;
  // Backend ISO timestamps retain microseconds; Date.parse truncates them.
  const fraction = (timestamp) => (/\.([0-9]+)(?:Z|[+-]\d{2}:\d{2})$/.exec(timestamp)?.[1] || "").padEnd(9, "0");
  return fraction(incoming.updated_at) < fraction(current.updated_at);
};

export const isActive = (job) =>
  job && !needsReview(job) && job.status !== "completed" && job.status !== "failed" && (
    job.status === "processing" || ["submitting", "processing"].includes(job.provider_status) ||
    ["reserving", "reserved", "settling", "releasing"].includes(job.credit_status)
  );

// Match the backend mutation guard: terminal settled/released jobs remain
// editable/deletable, while prepared or unresolved lifecycle work is protected.
export const isMutationLocked = (job) => Boolean(job && (
  needsReview(job) || ["reserving", "reserved", "settling", "releasing"].includes(job.credit_status) ||
  ["prepared", "submitting", "processing"].includes(job.provider_status) ||
  ["processing", "reconciling"].includes(job.status) ||
  (job.provider_status === "completed" && job.status !== "completed")
));

export const statusLabel = (job) => {
  if (!job) return "Draft";
  const state = needsReview(job) ? (RECONCILIATION.includes(job.credit_status) ? job.credit_status
    : RECONCILIATION.includes(job.provider_status) ? job.provider_status : "reconciling")
    : job.status === "failed" ? (job.provider_status === "timeout" ? "timeout" : "failed")
    : job.status === "completed" ? "completed" : job.credit_status === "quoted" ? "quoted"
    : job.provider_status || job.status;
  return ({ draft: "Draft", concept_ready: "Konsep Siap", prepared: "Siap diproses", quoted: "Persiapan kredit",
    submitting: "Mengirim generasi", processing: "Memproses video",
    completed: job.status === "completed" ? "Video selesai" : "Menyimpan video",
    failed: "Generasi gagal", timeout: "Hasil belum pasti — perlu diperiksa", reconciling: "Penyelesaian perlu diperiksa",
    submission_unknown: "Submission perlu diperiksa", reservation_unknown: "Reservasi perlu diperiksa",
    settling_unknown: "Penyelesaian kredit perlu diperiksa", releasing_unknown: "Pengembalian kredit perlu diperiksa",
  })[state] || "Memproses";
};

// Coarse grouping for dashboard badges/counters: Draft, Memproses, Berhasil, Gagal, Perlu diperiksa.
export const statusGroup = (job) => {
  if (!job) return { key: "draft", label: "Draft", tone: "slate" };
  if (needsReview(job)) return { key: "review", label: "Perlu diperiksa", tone: "amber" };
  if (job.status === "completed") return { key: "success", label: "Berhasil", tone: "emerald" };
  if (job.status === "failed") return { key: "failed", label: "Gagal", tone: "red" };
  if (isActive(job) || job.provider_status === "processing" || job.status === "processing")
    return { key: "processing", label: "Memproses", tone: "blue" };
  return { key: "draft", label: "Draft", tone: "slate" };
};

// B12: dashboard counters keep unresolved work visible. A job whose provider outcome or credit is
// unconfirmed must never be counted as a draft.
export const dashboardCounts = (videos) => (videos || []).reduce((acc, job) => {
  const key = statusGroup(job).key;
  acc[key] = (acc[key] || 0) + 1;
  return acc;
}, {});

// B13: open the project that was clicked. Ownership is still decided by the backend.
export const salesVideoProjectPath = (id) => `/video-jualan?project=${encodeURIComponent(id)}`;

export const TONE_CLASS = {
  slate: "bg-slate-100 text-slate-600",
  blue: "bg-blue-50 text-blue-700",
  amber: "bg-amber-50 text-amber-700",
  emerald: "bg-emerald-50 text-emerald-700",
  red: "bg-red-50 text-red-700",
};
